// Models for everything that isn't a flying airframe: emplacements, ships,
// ground vehicles, structures, the air base, towns and trees
// (art/model-integration.md, Parts 5–7). aircraft_models.js is the flying
// half; this is the rest of the world.
//
// The pattern is the same everywhere: a unit builds its box silhouette exactly
// as it always has, asks here for its model, and when the promise lands swaps
// the model in — with the boxes kept as the far level of detail, so a slow
// connection, a portal that can't reach the files, or ?boxes all still play.

import * as THREE from "three";
import { loadModel, ModelInstance, modelsEnabled, ModelLOD, setLodLevels } from "./models.js";

// Level-of-detail distances per class of unit (Part 3.1): LOD1, LOD2, then
// the box silhouette. Tune with the LOD probe before and after.
export const LOD_DIST = {
  aircraft: [250, 700, 2000],
  vehicle: [150, 400, 1200],
  ship: [600, 1500, 4000],
  building: [250, 700, 2500],
};

/**
 * Load a model's LOD0/1/2 (whichever exist). Resolves to [t0, t1|null, t2|null],
 * or null when models are off or LOD0 failed.
 *
 * `lods: false` loads LOD0 alone, for the ships. Their first LOD1/LOD2 hulls
 * were decimated straight across the boot-top seam, so red anti-fouling and
 * black boot paint climbed the topsides in long slivers (the Essex read as
 * dazzle-painted, the Liberty as red-bellied) at exactly the 600 m–4 km a
 * fleet is seen from. The art is fixed (model-integration.md 9.7: every hull
 * rides every level undecimated, and models.test.mjs holds the bottom paint
 * under the waterline and every turret on its pivot at every level), so
 * dropping `lods: false` here is now a client call for a playtest to make,
 * not an art bug to wait on.
 */
export async function loadLevels(stem, { lods = true } = {}) {
  if (!modelsEnabled()) return null;
  const stems = lods ? [stem, `${stem}_lod1`, `${stem}_lod2`] : [stem];
  const res = await Promise.allSettled(stems.map((s) => loadModel(s)));
  const tpls = res.map((r) => (r.status === "fulfilled" ? r.value : null));
  return tpls[0] ? tpls : null;
}

/**
 * Build a THREE.LOD over a model's levels, painted for a team, with `far` (the
 * box silhouette) as the last level. Returns { lod, levels: ModelInstance[] }.
 */
export function lodOf(tpls, { team = 0, scheme = null, dists = LOD_DIST.vehicle, far = null } = {}) {
  const lod = new ModelLOD();
  const levels = tpls.filter(Boolean).map((t) => new ModelInstance(t, { team, scheme }));
  const list = levels.map((m, i) => [m.root, i === 0 ? 0 : dists[i - 1]]);
  if (far) list.push([far, dists[2]]);
  setLodLevels(lod, list);
  return { lod, levels };
}

/** A model's wreck (the `_wreck.glb` beside it), painted; null on failure. */
export async function wreckOf(stem, team = 0) {
  if (!modelsEnabled()) return null;
  try {
    return new ModelInstance(await loadModel(`${stem}_wreck`), { team });
  } catch {
    return null;
  }
}

/**
 * Undercarriage boxes (planes.js mergeBoxes format) for a parked model
 * airframe whose root sits `lift` metres above the deck: main legs under the
 * wing, a tail leg aft. The art flies wheels-up; a reserve plane on the
 * flightline has to stand on something.
 */
export function parkedGearBoxes(a, lift = 1.7) {
  const deck = -lift;
  const tire = 0.36;
  const axle = deck + tire;
  const gx = a.wing.x + 0.25;
  const gz = Math.max(1.0, Math.min(a.wing.tipZ * 0.24, 3.6));
  const top = a.wing.y - a.wing.thick * 0.5;
  const legH = Math.max(0.3, top - axle);
  const tx = a.tail.x + 1.1;
  const ttop = a.tail.y - 0.35;
  const tlegH = Math.max(0.25, ttop - (deck + 0.22));
  const dark = 0x2b2f36, rubber = 0x16171a;
  const out = [];
  for (const s of [1, -1]) {
    out.push({ x: gx, y: axle + legH / 2, z: s * gz, w: 0.24, h: legH, d: 0.24, color: dark });
    out.push({ x: gx, y: axle, z: s * gz, w: tire * 2, h: tire * 2, d: 0.34, color: rubber });
  }
  out.push({ x: tx, y: deck + 0.22 + tlegH / 2, z: 0, w: 0.2, h: tlegH, d: 0.2, color: dark });
  out.push({ x: tx, y: deck + 0.22, z: 0, w: 0.44, h: 0.44, d: 0.24, color: rubber });
  return out;
}

// --- emplacements (Part 6) -----------------------------------------------------
// Each is <Root> → _Base (fixed) → _Mount (turns on Y) → _Gun (elevates on Z),
// barrels level along +X at rest. `gunY`/`gunX` place the gun trunnion in the
// model (the server's AAGun Pos sits there), `pivotY` is the trunnion's height
// above whatever the mount stands on (the ground, or a tub floor once the base
// is dropped), `muzzle` how far out along the gun's +X the rounds leave, and
// `base: false` drops the base's own meshes (a naval mount stands in a tub
// that's part of the hull).
export const EMPLACEMENTS = {
  // Shore flak: every land site fires the Flak 88's profile. It stands on the
  // server's flak pad (aaa.go flakPadY) with an earth plinth `plinthR` wide.
  flak88: { stem: "flak88", gunY: 1.99, gunX: 0.1, pivotY: 1.99, muzzle: 4.95, base: true, plinthR: 6.4 },
  // The twin Bofors on a warship's deck-edge tub (Part 5.1): mount 0.35 up
  // the model, trunnion 1.15 above that.
  bofors40: { stem: "bofors40_twin", gunY: 1.5, gunX: 0, pivotY: 1.15, muzzle: 2.72, base: false },
  // Fort Brick's crown gun and the Gulf coast pads (Part 6c).
  // Stands on the server's pad (armada.go padY) with a concrete plinth.
  coastal: { stem: "coastal_battery", gunY: 2.8, gunX: 3.4, pivotY: 2.8, muzzle: 10.6, base: true, mount: /_Turret$/, plinthR: 9.2, plinthColor: 0x8a8a84, tubes: [0] },
  // A shore searchlight (Part 6c): the lamp is the "gun" that pitches.
  searchlight: { stem: "searchlight", gunY: 1.45, gunX: 0, pivotY: 1.45, muzzle: 0.63, base: true, gun: /_Lamp$/ },
};

// A steel lattice tower (planes.js mergeBoxes format) from `bottom` (negative,
// below the platform) up to the platform at y 0: four legs `half` out from the
// centreline, cross-braced every few metres, a deck plate on top. What a shore
// searchlight stands on — the server lights the beam from 6 m up, where the
// old pylon's head was, and the Flak-pit-sized lamp model is 2.3 m tall.
export function towerBoxes(bottom, half) {
  const h = -bottom;
  const leg = 0.28;
  const steel = 0x4a4d52, dark = 0x33363b;
  const out = [];
  for (const [x, z] of [[half, half], [half, -half], [-half, half], [-half, -half]]) {
    out.push({ x, y: bottom + h / 2, z, w: leg, h, d: leg, color: steel });
  }
  for (let y = bottom + 1.6; y < -0.6; y += 2.2) {
    out.push({ x: 0, y, z: half, w: half * 2, h: 0.16, d: 0.16, color: dark });
    out.push({ x: 0, y, z: -half, w: half * 2, h: 0.16, d: 0.16, color: dark });
    out.push({ x: half, y, z: 0, w: 0.16, h: 0.16, d: half * 2, color: dark });
    out.push({ x: -half, y, z: 0, w: 0.16, h: 0.16, d: half * 2, color: dark });
  }
  out.push({ x: 0, y: -0.15, z: 0, w: half * 2 + 0.8, h: 0.3, d: half * 2 + 0.8, color: steel });
  return out;
}

/**
 * Load an emplacement model as a LOD whose levels each expose their own
 * mount and gun nodes, so one aim can drive whichever level is showing.
 * Resolves to { lod, levels: [{ inst, mount, gun }] } or null.
 */
export async function emplacementOf(spec, team) {
  const tpls = await loadLevels(spec.stem);
  if (!tpls) return null;
  const { lod, levels } = lodOf(tpls, { team, dists: LOD_DIST.vehicle });
  const out = levels.map((inst) => {
    const mount = inst.nodes(spec.mount ?? /_Mount$/)[0] ?? null;
    const gun = inst.nodes(spec.gun ?? /_Gun$/)[0] ?? null;
    if (!spec.base) {
      const base = inst.nodes(/_Base$/)[0];
      // Drop the base's own meshes, keep the mount standing on its pivot.
      for (const c of [...(base?.children ?? [])]) if (c.isMesh) base.remove(c);
    }
    return { inst, mount, gun };
  });
  if (out.some((l) => !l.mount || !l.gun)) return null;
  return { lod, levels: out };
}

// The earthwork a shore pit is dug into, as a mesh: a squat cylinder from the
// pad (y 0, the model's base) down to `bottom` (negative, below the coarse
// ground). Shared material; one geometry per height bucket.
const plinthMaterials = new Map();
const plinthGeos = new Map();
export function plinthMesh(radius, depth, color = 0x5f5140) {
  let plinthMaterial = plinthMaterials.get(color);
  if (!plinthMaterial) {
    plinthMaterial = new THREE.MeshLambertMaterial({ color });
    plinthMaterials.set(color, plinthMaterial);
  }
  const h = Math.max(0.4, Math.ceil(depth * 2) / 2); // half-metre buckets
  const key = `${radius}|${h}`;
  let geo = plinthGeos.get(key);
  if (!geo) {
    geo = new THREE.CylinderGeometry(radius * 0.97, radius * 1.06, h, 14);
    geo.translate(0, -h / 2 - 0.01, 0);
    plinthGeos.set(key, geo);
  }
  return new THREE.Mesh(geo, plinthMaterial);
}

// --- ground vehicles (Part 6b) ---------------------------------------------------
// Origin on the ground under the hull centre, track bottoms at y 0, front +X.
// `turret`/`gun` match the prefix-less names of the node that trains on Y and
// the one that elevates on Z (a halftrack's flak mount, a gun truck's pedestal
// count as its "turret"); `muzzles` are barrel tips in the gun's own frame;
// `tracks` is the tread scroll (links per metre, Part 3.3) and the half-track-
// width a pivot turn scrubs against.
export const VEHICLE_MODELS = {
  sherman: { stem: "sherman", turret: /^Turret$/, gun: /^Gun$/, muzzles: [[3.1, 0, 0]], tracks: { pitch: 0.17, halfW: 1.1 } },
  flak_halftrack: {
    stem: "flak_halftrack",
    turret: /^FlakMount$/,
    gun: /^FlakGun$/,
    muzzles: [[1.62, 0.2, 0.22], [1.62, 0.2, -0.22], [1.62, -0.2, 0.22], [1.62, -0.2, -0.22]],
    tracks: { pitch: 0.139, halfW: 0.95 },
  },
  m8_greyhound: { stem: "m8_greyhound", turret: /^Turret$/, gun: /^Gun$/, muzzles: [[2.02, 0, 0]] },
  m1_abrams: { stem: "m1_abrams", turret: /^Turret$/, gun: /^Gun$/, muzzles: [[5.45, 0, 0]], tracks: { pitch: 0.179, halfW: 1.45 } },
  m2_bradley: { stem: "m2_bradley", turret: /^Turret$/, gun: /^Gun$/, muzzles: [[2.55, 0, 0]], tracks: { pitch: 0.181, halfW: 1.4 } },
  // The recoilless rifle's back-blast gets a flash of its own out the venturi.
  jackal: { stem: "jackal_m151_m40", turret: /^Mount$/, gun: /^Gun$/, muzzles: [[2.25, 0, 0], [-1.7, 0, 0]] },
};

// Which model each roster slot drives: the air game's column vehicles
// (groundVehicles.types: Sherman, Sd.Kfz. 7/1, Greyhound) and the land
// theater's (land.vehicles: Abrams, Bradley, Sd.Kfz. 7/1, MUTT).
export const AIR_VEHICLE_MODEL = ["sherman", "flak_halftrack", "m8_greyhound"];
export const LAND_VEHICLE_MODEL = ["m1_abrams", "m2_bradley", "flak_halftrack", "jackal"];

/**
 * A vehicle model's per-level live parts: the training turret and elevating
 * gun, and per-side tread materials cloned for this one vehicle so its tracks
 * scroll on their own (and against each other in a pivot turn).
 */
export function vehicleParts(levels, spec) {
  return levels.map((inst) => {
    const turret = inst.nodes(spec.turret)[0] ?? null;
    const gun = inst.nodes(spec.gun)[0] ?? null;
    const treads = { L: [], R: [] };
    if (spec.tracks) {
      for (const side of ["L", "R"]) {
        const node = inst.nodes(new RegExp(`^Track_${side}$`))[0];
        node?.traverse((o) => {
          if (!o.isMesh || !o.material?.map) return;
          const m = o.material.clone();
          m.map = o.material.map.clone();
          m.map.wrapS = THREE.RepeatWrapping;
          m.map.needsUpdate = true;
          o.material = m;
          treads[side].push(m);
        });
      }
    }
    return { inst, turret, gun, treads };
  });
}

/** Scroll a vehicle's treads: signed metres run by each side this frame. */
export function scrollTreads(parts, spec, dL, dR) {
  if (!spec.tracks) return;
  const p = spec.tracks.pitch;
  for (const lv of parts) {
    for (const m of lv.treads.L) m.map.offset.x += dL / p;
    for (const m of lv.treads.R) m.map.offset.x += dR / p;
  }
}

/** Free one vehicle's cloned tread materials. */
export function disposeTreads(parts) {
  for (const lv of parts ?? []) {
    for (const m of [...lv.treads.L, ...lv.treads.R]) {
      m.map?.dispose();
      m.dispose();
    }
  }
}

// --- warships (Part 5.4) ---------------------------------------------------------
// The naval theater's crewable hulls at real scale, by config name. Origin on
// the waterline at the hull's centre — the Iowa's model sits 11.9 m forward
// of its own origin, so `shift` slides it back and the server's hull box stays
// symmetric. `battery` is the model turret each config turret
// (mainGun.turrets, same order) fires from, and shared/config.json carries
// those turrets' own numbers (fwdM, mountY, barrelM) so the round leaves the
// barrel you can see. `mimic` is every other mount on the model: they train
// with the battery and fire nothing, the doctrine that keeps a Fletcher's five
// 5-inchers honest about her two-gun broadside. A mimic off the centreline is
// a beam mount and bears only on its own rail; a mimic with no gun node (a
// torpedo mount) just trains. `muzzle` is how far down the gun node's X the
// rifles end, `tubes` their z, `deck` the amidships main deck the box stand-in
// is stretched to, `top` the masthead a foundering hull must sink past.
export const NAVAL_MODELS = {
  Fletcher: {
    stem: "fletcher",
    battery: ["Mount52", "Mount53"],
    mimic: ["Mount51", "Mount54", "Mount55", "Torpedo1", "Torpedo2"],
    muzzle: 5.2,
    tubes: [0],
    deck: 4.4,
    top: 28.5,
  },
  County: {
    stem: "county",
    battery: ["TurretB", "TurretX"],
    mimic: ["TurretA", "TurretY", "HA_P0", "HA_P12", "HA_P24", "HA_Pm12", "HA_S0", "HA_S12", "HA_S24", "HA_Sm12"],
    muzzle: 10.25,
    tubes: [1.0, -1.0],
    deck: 9.0,
    top: 39,
  },
  Iowa: {
    stem: "iowa_realscale",
    shift: -11.9,
    battery: ["TurretA", "TurretB", "TurretX"],
    mimic: [],
    muzzle: 19.76,
    tubes: [2.08, 0, -2.08],
    deck: 11.0,
    top: 44.2,
  },
  "Des Moines": {
    stem: "des_moines",
    battery: ["TurretA", "TurretB", "TurretX"],
    mimic: ["Sec_P22", "Sec_P4", "Sec_Pm28", "Sec_S22", "Sec_S4", "Sec_Sm28"],
    muzzle: 13.85,
    tubes: [2.25, 0, -2.25],
    deck: 8.0,
    top: 39.2,
  },
  // The premium super-battleship: three triple 46 cm turrets, the two 15.5 cm
  // triples on the centreline (one superfiring forward, one over the quarterdeck)
  // and six twin 12.7 cm HA mounts on the beams — all of them train with the
  // battery, none of them fire anything the config doesn't.
  Yamato: {
    stem: "yamato",
    battery: ["TurretA", "TurretB", "TurretX"],
    mimic: ["SecFwd", "SecAft", "HA_P18", "HA_P6", "HA_Pm6", "HA_S18", "HA_S6", "HA_Sm6"],
    muzzle: 21.0,
    tubes: [3.2, 0, -3.2],
    deck: 8.7,
    top: 42.2,
  },
  Osa: {
    stem: "osa_missile_boat",
    battery: ["GunFwd"],
    mimic: ["GunAft"],
    muzzle: 2.45,
    tubes: [0.17, -0.17],
    deck: 2.4,
    top: 12.1,
    launchers: ["Launcher1", "Launcher2", "Launcher3", "Launcher4"],
  },
};
// The roster order the naval tables are indexed by when a config hull carries
// no name (destroyer, cruiser, battleship, heavy cruiser, missile boat).
const NAVAL_ORDER = ["Fletcher", "County", "Iowa", "Des Moines", "Osa"];

/** The real-scale model spec for a warship config type (or null). */
export function navalModelOf(t, index) {
  return NAVAL_MODELS[t?.name] ?? NAVAL_MODELS[NAVAL_ORDER[index]] ?? null;
}

/**
 * A warship model's per-level live parts: for each battery turret and mimic
 * mount, its training node and elevating gun node (null for a torpedo mount),
 * plus its rest facing (0 bow, π built facing aft) and rail (−1 port, +1
 * starboard, 0 centreline) read off the model. Null if a battery turret is
 * missing — the box mounts then stay up rather than aiming half a ship.
 */
export function warshipParts(levels, spec) {
  const out = [];
  for (const inst of levels) {
    const mount = (name) => {
      const node = inst.node(name);
      if (!node) return null;
      const gun = node.children.find((c) => /_Guns?$/.test(c.name)) ?? null;
      // An aft-built mount carries its half-turn as rot(π, 0, π): read the
      // facing off where its local +X points.
      const fx = new THREE.Vector3(1, 0, 0).applyQuaternion(node.quaternion).x;
      return { node, gun, rest: fx < 0 ? Math.PI : 0, side: Math.abs(node.position.z) > 1 ? Math.sign(node.position.z) : 0 };
    };
    const battery = spec.battery.map(mount);
    if (battery.some((b) => !b)) return null;
    const launchers = (spec.launchers ?? []).map((n) => inst.node(n)).filter(Boolean);
    out.push({ inst, battery, mimic: spec.mimic.map(mount).filter(Boolean), launchers });
  }
  return out;
}

// --- structures (Part 6c) --------------------------------------------------------
// Shore installations stand on the server's pad (aaa.go padY, 3 m under the
// hit-sphere centre) at real size — the box era scaled them up 1.6x to read
// from altitude; the models are big enough already.
export const STRUCTURES = {
  0: { stem: "fuel_farm", plinthR: 12.5, plinthColor: 0x7d7a72 }, // fuel depot
  1: { stem: "radar_station", plinthR: 9.5, plinthColor: 0x8a8a84 }, // radar station
};
// The radar's Würzburg dish sweeps while the station stands (the "radiates"
// cue): a slow circle, a touch of nod in the dish.
export const RADAR_SWEEP_RAD_S = 0.45;

// The barrage balloon (Part 6c): the model's balloon node flies at the
// server's balloon position; its cable ring hangs 6.3 m under that origin,
// and the cable is a 1 m unit tube stood on the winch 2 m up, stretched to
// reach it.
export const BALLOON = { stem: "barrage_balloon", ringDrop: 6.3, cableBase: 2 };

// --- towns (Part 6d) -------------------------------------------------------
// The street grid (terrain_core townBuilding, mirrored by land.go) is the
// server's: every lot is a w × h × d collision box and the drawing only
// dresses it. A lot's height class picks the family — cottage or barn under
// a 6 m ridge, house or townhouse at 10, shop, apartment block or town hall
// at 14 — and the model is stretched to fill the box, door turned to the
// nearest main street.
export const TOWN_CLASSES = {
  6: ["cottage", "barn"],
  10: ["house", "townhouse"],
  14: ["shop", "apartment", "town_hall"],
};
// Wall footprint [along the door axis (model X), across it (model Z), and
// where along X its centre sits], m — art/model-integration.md 1f
// (town_manifest.json footprintXZ). Every lot building is centred on its
// origin; the church isn't: apse wall at x −16.2 to tower face at +11.5.
export const TOWN_FOOTPRINT = {
  cottage: [8, 12],
  barn: [14, 9],
  house: [9, 12],
  townhouse: [10, 8],
  shop: [11, 11],
  apartment: [12, 14],
  town_hall: [12, 18],
  church: [27.7, 10, -2.35],
};
// Two candidates whose stretch is within this (log-ratio) of each other are
// a toss-up the lot hash decides; beyond it the better fit wins, so a deep
// lot gets the deep building rather than a cottage pulled like toffee.
const TOWN_FIT_TIE = 0.25;

/**
 * How to dress one town lot: { stem, yaw, sx, sz, x, z } — the model's
 * origin (x, z) is the lot centre unless its footprint is off-centre. `b` is a townBuilding
 * lot, `t` its town, `grid` the street pitch, `n` the lot's [0,1) hash, and
 * `hallTaken` whether this town already has its town hall (one per town,
 * and never on a lot fronting a main street, where its 4.6 m portico would
 * stand in the road). Pure and deterministic — every client dresses the
 * same town the same way.
 */
export function townLot(b, t, grid, n, hallTaken = false) {
  const gx = Math.floor((b.x - t.x) / grid);
  const gz = Math.floor((b.z - t.z) / grid);
  // The main streets are the gx = 0 and gz = 0 rows (x, z ∈ [0, grid) from
  // the centre): a lot east of one faces west onto it, and so on. Yaw turns
  // the model's +X door: 0 → +X, π → −X, −π/2 → +Z, +π/2 → −Z.
  const ax = Math.abs(gx), az = Math.abs(gz);
  const alongX = b.church || ax <= az; // the church's nave is the lot's X
  const yaw = alongX ? (gx > 0 ? Math.PI : 0) : gz > 0 ? Math.PI / 2 : -Math.PI / 2;
  // Lot extent along the model's X (the door axis) and across it.
  const lx = alongX ? b.w : b.d;
  const lz = alongX ? b.d : b.w;
  const place = (stem, sx, sz) => {
    // Put the footprint's centre, not the origin, on the lot centre.
    const cx = (TOWN_FOOTPRINT[stem][2] ?? 0) * sx;
    return { stem, yaw, sx, sz, x: b.x - cx * Math.cos(yaw), z: b.z + cx * Math.sin(yaw) };
  };
  if (b.church) {
    const [fx, fz] = TOWN_FOOTPRINT.church;
    return place("church", lx / fx, lz / fz);
  }
  const frontage = Math.min(ax, az) <= 1;
  const cands = (TOWN_CLASSES[b.h] ?? TOWN_CLASSES[10]).filter(
    (s) => s !== "town_hall" || (!hallTaken && !frontage),
  );
  const fit = cands
    .map((stem) => {
      const [fx, fz] = TOWN_FOOTPRINT[stem];
      const sx = lx / fx, sz = lz / fz;
      return { stem, yaw, sx, sz, err: Math.abs(Math.log(sx / sz)) };
    })
    .sort((a, c) => a.err - c.err);
  const ties = fit.filter((f) => f.err - fit[0].err <= TOWN_FIT_TIE);
  const pick = ties[Math.min(ties.length - 1, Math.floor(n * ties.length))];
  return place(pick.stem, pick.sx, pick.sz);
}
